import type { CenterOfInfluenceRepSpecialty } from '@savvy/constants/generated/gql-enums';

import type { ContactWebGraphEdge, ContactWebGraphNode } from './types';

export type ContactWebFilters = {
  readonly advisorNames: ReadonlySet<string>;
  readonly specialties: ReadonlySet<CenterOfInfluenceRepSpecialty>;
};

export const EMPTY_CONTACT_WEB_FILTERS: ContactWebFilters = { advisorNames: new Set(), specialties: new Set() };

export function isContactWebFilterActive(filters: ContactWebFilters): boolean {
  return filters.advisorNames.size > 0 || filters.specialties.size > 0;
}

function nodeMatchesFilters(node: ContactWebGraphNode, filters: ContactWebFilters): boolean {
  if (node.kind === 'household') {
    return node.advisorName !== null && filters.advisorNames.has(node.advisorName);
  }
  return node.specialties.some((specialty) => filters.specialties.has(specialty));
}

/**
 * Nodes matching the filter, plus everyone directly connected to a match over `edges` (1 hop,
 * either direction). Expansion is computed from the original matched set only, so it can't
 * cascade past 1 hop regardless of edge iteration order. Returns null when no filter is active —
 * callers treat null as "show everything".
 */
export function filterVisibleNodeIds(
  nodes: readonly ContactWebGraphNode[],
  edges: readonly ContactWebGraphEdge[],
  filters: ContactWebFilters,
): ReadonlySet<string> | null {
  if (!isContactWebFilterActive(filters)) return null;

  const matchedIds = new Set(nodes.filter((node) => nodeMatchesFilters(node, filters)).map((node) => node.id));
  const visible = new Set(matchedIds);
  for (const edge of edges) {
    if (matchedIds.has(edge.source)) visible.add(edge.target);
    if (matchedIds.has(edge.target)) visible.add(edge.source);
  }
  return visible;
}
